'use client';

import { useAuth } from '@/components/Auth';
import React, { useState, useEffect } from 'react';
import type { IUser, TUserRole } from '@/types';

const DevAuthSwitcher = () => {
  const { user, login, logout } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Only show in development or local environments
  // Hide when forcing SSO authentication (NEXT_PUBLIC_FORCE_SSO_AUTH=true)
  const forceSSOAuth = process.env.NEXT_PUBLIC_FORCE_SSO_AUTH === 'true';
  const isDevelopment = process.env.NEXT_PUBLIC_APP_ENV === 'development';
  const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost';
  
  if (!isClient || forceSSOAuth || (!isDevelopment && !isLocalhost)) {
    return null;
  }

  const switchToRole = (role: TUserRole) => {
    const devUser: IUser = {
      uuid: `dev-${role?.toLowerCase().replace(' ', '-')}-001`,
      username: `dev-${role?.toLowerCase().replace(' ', '-')}`,
      name: `Dev ${role}`,
      email: `dev-${role?.toLowerCase().replace(' ', '-')}@fieldone.com`,
      role,
      imageUrl: role === 'rep' ? '/public/user-profiles/aloomis.jpg' : role === 'dm' ? '/public/user-profiles/TKim45.jpg' : '/public/user-profiles/cpirone.jpg',
      createdAt: new Date(),
      updatedAt: new Date()
    };

    login(devUser);
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 p-2">
      {!isExpanded ? (
        // Collapsed icon state
        <button
          onClick={() => setIsExpanded(true)}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-yellow-400 bg-yellow-100 shadow-lg hover:bg-yellow-200"
          title={`Dev Mode - Current: ${user?.name || 'Not logged in'}`}
        >
          <span className="pointer-events-none text-sm">{`🛠️`}</span>
        </button>
      ) : (
        // Expanded panel state
        <div className="rounded-lg border border-yellow-400 bg-yellow-100 p-4 shadow-lg min-w-[220px]">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-xs font-semibold text-yellow-800">🛠️ DEV MODE</div>
            <button
              onClick={() => setIsExpanded(false)}
              className="text-xs text-yellow-600 hover:text-yellow-800"
            >
              <span className="pointer-events-none">{`x`}</span>
            </button>
          </div>
          <div className="mb-2 text-xs text-yellow-700">
            Current: {user?.name || 'Not logged in'}
          </div>
          <div className="flex flex-col gap-2">
            <button
              onClick={() => switchToRole('developer')}
              className="rounded bg-blue-500 px-3 py-1 text-xs text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:hover:bg-blue-500"
              disabled={user?.role === 'developer'}
            >
              <span className="pointer-events-none">Developer</span>
            </button>
            <button
              onClick={() => switchToRole('rep')}
              className="rounded bg-blue-500 px-3 py-1 text-xs text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:hover:bg-blue-500"
              disabled={user?.role === 'rep'}
            >
              <span className="pointer-events-none">Sales Rep</span>
            </button>
            <button
              onClick={() => switchToRole('dm')}
              className="rounded bg-green-500 px-3 py-1 text-xs text-white hover:bg-green-600 disabled:cursor-not-allowed disabled:hover:bg-green-500"
              disabled={user?.role === 'Manager'}
            >
              <span className="pointer-events-none">Manager</span>
            </button>
            <button
              onClick={logout}
              className="rounded bg-red-500 px-3 py-1 text-xs text-white hover:bg-red-600 disabled:cursor-not-allowed disabled:hover:bg-red-500"
            >
              <span className="pointer-events-none">Logout</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DevAuthSwitcher;
